import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, FileText, Loader2, Printer, Send, Wrench } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { openDocument } from "@/lib/documents";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";
import { useIndent, useSubmitIndent } from "@/features/indents/api";
import { IndentPriorityBadge, IndentStatusBadge, IndentAllocationBadge, StageChip } from"@/features/indents/badges";
import { DynamicApprovalPanel } from "@/features/indents/DynamicApprovalPanel";
import { PurchaseActionPanel } from "@/features/indents/PurchaseActionPanel";
import { InstallationDialog } from "@/features/indents/InstallationDialog";
import { CycleTimeline } from "@/features/indents/CycleTimeline";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { PoAllocationCard } from "@/features/purchase-orders/PoAllocationCard";
import { getSupabase } from "@/integrations/external-supabase/client";
import { fmtDateTime } from "@/lib/utils";

const INSTALL_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);

export const Route = createFileRoute("/_authenticated/indents/$indentId/")({
  component: IndentDetailPage,
});

function currentStage(i: ReturnType<typeof useIndent>["data"]): string {
  if (!i) return "—";
  if (i.closed_at) return "Closed";
  if (i.installation_completed_at) return "Installation Completed";
  if (i.asset_created_at) return "Asset Created · Installation Pending";
  if (i.grn_received_at) return "GRN Received · Asset Pending";
  if (i.status === "PO Raised") return "PO Raised · GRN Pending";
  if (i.status === "Purchase Approved") return "Purchase Approved · PO Pending";
  if (i.status === "Technical Review") return "Technical Approved · Pending Purchase";
  return i.status;
}

async function openDoc(path: string | null) {
  if (!path) return;
  const w = /^https?:\/\//i.test(path) || path.startsWith("/media/") ? null : window.open("about:blank", "_blank");
  try {
    if (/^https?:\/\//i.test(path) || path.startsWith("/media/")) {
      if (w) w.location.href = path;
      else window.location.href = path;
      return;
    }
    const sb = getSupabase();
    const { data, error } = await sb.storage.from("asset-docs").createSignedUrl(path, 300);
    if (error || !data?.signedUrl) throw error ?? new Error("no url");
    if (w) w.location.href = data.signedUrl;
    else window.location.href = data.signedUrl;
  } catch (e) {
    if (w) w.close();
    toast.error((e as Error).message);
  }
}

function IndentDetailPage() {
  const { indentId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useCurrentUser();
  const { data: indent, isLoading } = useIndent(indentId);
  const submit = useSubmitIndent();

  if (isLoading) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!indent) return <div className="p-6 text-sm text-muted-foreground">Indent not found.</div>;

  const roles = me?.roles ?? [];
  const canInstall = roles.some((r) => INSTALL_ROLES.has(r));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/indents" })}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => openDocument(`/api/v1/procurement/indents/${indent.id}/document/`).catch((e) => toast.error((e as Error).message))}>
            <Printer className="mr-1 h-4 w-4" />Print / PDF
          </Button>
          {indent.status === "Draft" && (      // same rule as the list; the server enforces who may edit / submit
            <>
              <Button size="sm" variant="outline" onClick={() => navigate({ to: "/indents/$indentId/edit", params: { indentId: indent.id } })}>
                <Wrench className="mr-1 h-4 w-4" />Edit
              </Button>
              <Button size="sm" onClick={async () => { try { await submit.mutateAsync(indent.id); toast.success("Submitted"); } catch (e) { toast.error((e as Error).message); } }}>
                <Send className="mr-1 h-4 w-4" />Submit for Approval
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span><span className="font-mono text-base mr-2">{indent.indent_no}</span>{indent.item_desc}</span>
            <span className="flex gap-2 flex-wrap">
              <IndentPriorityBadge priority={indent.priority} />
              <IndentAllocationBadge status={indent.status} allocation={indent.po_allocation} />
              {currentStage(indent) !== indent.status && <StageChip label={currentStage(indent)} tone={indent.closed_at ? "done" : "pending"} />}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-4">
          <div><div className="text-xs text-muted-foreground">Requested By</div><div>{indent.creator?.full_name || indent.creator?.email || "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Requester Department</div><div>{indent.creator?.department?.name ?? indent.department?.name ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Requested On</div><div>{indent.created_at ? fmtDateTime(indent.created_at) : "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Branch</div><div>{indent.branch?.name ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Department</div><div>{indent.department?.name ?? "—"}</div></div>
          {indent.source_store_indent && (
            <div><div className="text-xs text-muted-foreground">Source Store Indent</div>
              <Link to="/store-indents/$id" params={{ id: indent.source_store_indent }} className="text-primary hover:underline">{indent.source_store_indent_no}</Link></div>
          )}
          <div className="md:col-span-3"><div className="text-xs text-muted-foreground">Justification</div><div>{indent.justification || "—"}</div></div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          {(() => {
            const rows = indent.items && indent.items.length > 0
              ? [...indent.items].sort((a, b) => a.line_no - b.line_no)
              : [{
                  id: "legacy",
                  item_kind: indent.kind,
                  line_no: 1,
                  asset_type: null,
                  asset_category_id: indent.category_id,
                  item_name: indent.item_desc,
                  item_description: indent.justification,
                  quantity_required: indent.qty_required,
                  estimated_unit_cost: Number(indent.estimated_unit_cost ?? 0),
                  estimated_total_cost: indent.qty_required * Number(indent.estimated_unit_cost ?? 0),
                  priority: indent.priority,
                  remarks: null,
                  procurement_status: indent.purchase_order_id ? "PO Created" : "PO Pending",
                  asset_category: indent.category,
                  source_store_indent_item: null as string | null,
                  source_si_requested_qty: null as string | null,
                } as const];
            const totalQty = rows.reduce((s, r) => s + Number(r.quantity_required || 0), 0);
            const totalValue = rows.reduce((s, r) => s + Number(r.quantity_required || 0) * Number(r.estimated_unit_cost || 0), 0);
            return (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Kind / Type</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Est. Unit ₹</TableHead>
                      <TableHead className="text-right">Est. Total ₹</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead>Status</TableHead>
                      {indent.source_store_indent && <TableHead>Source SI Line</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs">{r.line_no}</TableCell>
                        <TableCell className="text-sm">
                          {r.item_kind && r.item_kind !== "asset"
                            ? <Badge variant="outline" className="text-xs capitalize">{r.item_kind}</Badge>
                            : <>{r.item_kind && <Badge variant="outline" className="text-xs mr-1">Asset</Badge>}{r.asset_type || (r.item_kind ? "" : "—")}</>}
                        </TableCell>
                        <TableCell className="text-sm">{r.item_name || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.item_description ?? "—"}</TableCell>
                        <TableCell className="text-right">{Number(r.quantity_required)}</TableCell>
                        <TableCell className="text-right">₹ {Number(r.estimated_unit_cost).toFixed(2)}</TableCell>
                        <TableCell className="text-right">₹ {Number(r.estimated_total_cost ?? Number(r.quantity_required) * Number(r.estimated_unit_cost)).toFixed(2)}</TableCell>
                        <TableCell className="text-xs">{r.priority ?? "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.remarks ?? "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{r.procurement_status}</Badge></TableCell>
                        {indent.source_store_indent && (
                          <TableCell className="text-xs text-muted-foreground">
                            {r.source_store_indent_item ? <>{indent.source_store_indent_no} · #{String(r.source_store_indent_item).slice(0, 8)} (SI qty {Number(r.source_si_requested_qty)})</> : "—"}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex flex-wrap gap-4 border-t p-3 text-xs text-muted-foreground">
                  <span>Total items: <b>{rows.length}</b></span>
                  <span>Total quantity: <b>{totalQty}</b></span>
                  <span>Estimated total: <b>₹ {totalValue.toFixed(2)}</b></span>
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>

      <PoAllocationCard indentId={indent.id} status={indent.status} />

      {/* Approval Trail — Dynamic, driven by Config Center rules */}
      {indent.status !== "Draft" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Approval Trail</CardTitle></CardHeader>
          <CardContent>
            <DynamicApprovalPanel
              entityId={indent.id}
              moduleKey="indents"
              approvalStatusUrl={`/api/v1/procurement/indents/${indent.id}/approval-status/`}
              approveUrl={`/api/v1/procurement/indents/${indent.id}/approve/`}
              onSuccess={() => {
                qc.invalidateQueries({ queryKey: ["indent", indent.id] });
                qc.invalidateQueries({ queryKey: ["indents"] });
              }}
            />
          </CardContent>
        </Card>
      )}


      <PurchaseActionPanel indent={indent} />

      {/* PO Card */}
      {indent.po_number && (
        <Card>
          <CardHeader><CardTitle className="text-base">Purchase Order</CardTitle></CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-4">
            <div><div className="text-xs text-muted-foreground">PO Number</div><div className="font-mono">{indent.po_number}</div></div>
            <div><div className="text-xs text-muted-foreground">PO Date</div><div>{indent.po_date}</div></div>
            <div className="md:col-span-2"><div className="text-xs text-muted-foreground">Vendor</div><div>{indent.po_vendor?.name ?? "—"}</div></div>
            <div><div className="text-xs text-muted-foreground">Basic</div><div>₹ {Number(indent.po_basic_value ?? 0).toFixed(2)}</div></div>
            <div><div className="text-xs text-muted-foreground">CGST</div><div>₹ {Number(indent.po_cgst ?? 0).toFixed(2)}</div></div>
            <div><div className="text-xs text-muted-foreground">SGST</div><div>₹ {Number(indent.po_sgst ?? 0).toFixed(2)}</div></div>
            <div><div className="text-xs text-muted-foreground">IGST</div><div>₹ {Number(indent.po_igst ?? 0).toFixed(2)}</div></div>
            <div><div className="text-xs text-muted-foreground">Total</div><div className="font-medium">₹ {Number(indent.po_total_value ?? 0).toFixed(2)}</div></div>
            <div><div className="text-xs text-muted-foreground">Expected Delivery</div><div>{indent.po_expected_delivery_date ?? "—"}</div></div>
            <div className="md:col-span-2"><div className="text-xs text-muted-foreground">Delivery Terms</div><div>{indent.po_delivery_terms ?? "—"}</div></div>
            {indent.po_remarks && <div className="md:col-span-4"><div className="text-xs text-muted-foreground">Remarks</div><div>{indent.po_remarks}</div></div>}
            {indent.po_document_url && (
              <div className="md:col-span-4">
                <Button variant="outline" size="sm" onClick={() => openDoc(indent.po_document_url)}>
                  <FileText className="mr-1 h-4 w-4" />View PO Document
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* GRN Card — only show for legacy indents that already have a GRN but no linked PO,
          or for new-flow indents once a GRN exists. Direct "Create GRN" is not available here;
          use the linked Purchase Order instead. */}
      {indent.grn_id && (
        <Card>
          <CardHeader><CardTitle className="text-base">Goods Receipt</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">GRN</div>
              <Link to="/grns/$grnId" params={{ grnId: indent.grn_id }} className="font-mono text-primary hover:underline">
                {indent.grn?.grn_no ?? indent.grn_id}
              </Link>
            </div>
            <div className="text-xs text-muted-foreground">Received {indent.grn_received_at ? fmtDateTime(indent.grn_received_at) : "—"}</div>
          </CardContent>
        </Card>
      )}
      {!indent.grn_id && indent.status === "PO Raised" && indent.purchase_order_id && (
        <Card>
          <CardHeader><CardTitle className="text-base">Goods Receipt</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="text-muted-foreground">No GRN recorded yet. Record it from the linked Purchase Order.</div>
            <Button size="sm" variant="outline" onClick={() => navigate({ to: "/purchase-orders/$poId", params: { poId: indent.purchase_order_id! } })}>
              <ExternalLink className="mr-1 h-4 w-4" />Open Purchase Order
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Asset Card */}
      {(indent.created_asset_id || indent.grn_received_at) && (
        <Card>
          <CardHeader><CardTitle className="text-base">Asset</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
            {indent.created_asset_id ? (
              <>
                <div>
                  <div className="text-xs text-muted-foreground">Created Asset</div>
                  <Link to="/assets/$assetId" params={{ assetId: indent.created_asset_id }} className="text-primary hover:underline">
                    <span className="font-mono mr-2">{indent.created_asset?.asset_code}</span>{indent.created_asset?.name}
                  </Link>
                </div>
                <div className="text-xs text-muted-foreground">Created {indent.asset_created_at ? fmtDateTime(indent.asset_created_at) : "—"}</div>
                <Button size="sm" onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: indent.created_asset_id! } })}>
                  <ExternalLink className="mr-1 h-4 w-4" />Allocate Asset
                </Button>
                {!indent.installation_completed_at && (
                  <div className="w-full text-xs text-amber-700 dark:text-amber-300">
                    Awaiting installation &amp; commissioning from asset detail.
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted-foreground">GRN received. Open the GRN to create the asset.</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Installation & Commissioning */}
      {indent.asset_created_at && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              Installation &amp; Commissioning
              {!indent.installation_completed_at && canInstall && (
                <InstallationDialog
                  indentId={indent.id}
                  trigger={<Button size="sm"><Wrench className="mr-1 h-4 w-4" />Record Installation</Button>}
                />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-4">
            {!indent.installation_completed_at ? (
              <div className="md:col-span-4 text-muted-foreground">Installation pending. Capture OEM engineer details and report.</div>
            ) : (
              <>
                <div><div className="text-xs text-muted-foreground">OEM Engineer</div><div>{indent.install_engineer_name ?? "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Contact</div><div>{indent.install_engineer_contact ?? "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Installation Date</div><div>{indent.installation_date ?? "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Commissioning Date</div><div>{indent.commissioning_date ?? "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Training Given</div><div>{indent.training_given ? "Yes" : "No"}</div></div>
                <div className="md:col-span-3"><div className="text-xs text-muted-foreground">Staff Trained</div><div>{indent.training_attendees ?? "—"}</div></div>
                {indent.installation_remarks && <div className="md:col-span-4"><div className="text-xs text-muted-foreground">Remarks</div><div>{indent.installation_remarks}</div></div>}
                {indent.installation_report_url && (
                  <div className="md:col-span-4">
                    <Button variant="outline" size="sm" onClick={() => openDoc(indent.installation_report_url)}>
                      <ExternalLink className="mr-1 h-4 w-4" />View Installation Report
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cycle Timeline */}
      <CycleTimeline indent={indent} />
    </div>
  );
}
